import { useDebounce } from '@/hooks/useDebounce';
import { Alert, Box, Button } from '@palmetto/palmetto-components';
import { useFormikContext } from 'formik';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import { useValidateChangeOrderQuery } from '../../services/changeOrders';

interface ChangeOrderSuggestionBannerProps {
  accountId: string;
  contractedSystemSize: number;
}

export function ChangeOrderSuggestionBanner({ accountId, contractedSystemSize }: ChangeOrderSuggestionBannerProps) {
  const { systemSizeChangeCalculator = false, easyChangeOrder = false } = useFlags();
  const { values } = useFormikContext<{ systemSizeKw?: string | number }>();
  const navigate = useNavigate();
  const { data: eligibility } = useValidateChangeOrderQuery(
    { accountId },
    { skip: !accountId || !systemSizeChangeCalculator || !easyChangeOrder },
  );

  // Debounce so the banner only evaluates once the user pauses typing — otherwise typing toward a
  // value that matches the contract (e.g. 7 → 7.5 against a 7.5 contract) briefly flashes the banner
  // on the intermediate "7". Debounce the raw field value, not the Number() result: an empty/undefined
  // field coerces to NaN, and NaN !== NaN would make useDebounce's equality guard loop forever.
  const debouncedSystemSize = useDebounce(values.systemSizeKw, 400);
  const enteredSize = Number(debouncedSystemSize);
  const hasSizeMismatch = Number.isFinite(enteredSize) && enteredSize > 0 && enteredSize !== contractedSystemSize;

  const goToCreateChangeOrder = useCallback(() => {
    navigate(`/accounts/${accountId}/change-order/create?systemSizeKw=${enteredSize}`);
  }, [navigate, accountId, enteredSize]);

  // Both flags must be on: systemSizeChangeCalculator enables the suggestion itself, and
  // easyChangeOrder gates the create route the CTA leads to — suggesting a change order the user
  // can't actually create (the create route is FlagGuard'd on easyChangeOrder) is a dead end.
  if (!systemSizeChangeCalculator || !easyChangeOrder || !hasSizeMismatch || !eligibility?.eligible) {
    return null;
  }

  return (
    <Box margin="0 0 xs 0" padding={{ base: '0 0 0 0', tablet: '0 0 xs 0' }}>
      <Alert
        data-testid="change-order-suggestion-banner"
        variant="warning"
        hasIcon
        message={
          <Box childGap="sm">
            <Box>
              {`Your as-built system size (${enteredSize} kW DC) differs from your contracted size (${contractedSystemSize} kW DC). Consider creating a change order before submitting your install package.`}
            </Box>
            <Box>
              <Button
                variant="secondary"
                tone="primary"
                size="sm"
                onClick={goToCreateChangeOrder}
                style={{ maxWidth: 'fit-content' }}
              >
                Create Change Order
              </Button>
            </Box>
          </Box>
        }
      />
    </Box>
  );
}
